/**
 * 权限包装组件
 * 借鉴若依框架的v-hasPermi指令思路，适配React
 */
import React from 'react'
import { useAppSelector } from '../store/hooks'

interface PermissionWrapperProps {
  permission?: string | string[]
  role?: string | string[]
  children: React.ReactNode
  fallback?: React.ReactNode
}

/**
 * 权限包装组件
 * 用法：
 * <PermissionWrapper permission="textbook:chapter:add">
 *   <Button>新增章节</Button>
 * </PermissionWrapper>
 */
export const PermissionWrapper: React.FC<PermissionWrapperProps> = ({
  permission,
  role,
  children,
  fallback = null
}) => {
  const permissions = useAppSelector(state => state.auth.permissions || [])
  const roles = useAppSelector(state => state.auth.roles || [])

  // 权限检查
  if (permission) {
    const hasPermission = Array.isArray(permission)
      ? permission.some(p => permissions.includes(p))
      : permissions.includes(permission)
    
    if (!hasPermission) {
      return <>{fallback}</>
    }
  }

  // 角色检查
  if (role) {
    const hasRole = Array.isArray(role)
      ? role.some(r => roles.includes(r))
      : roles.includes(role)
    
    if (!hasRole) {
      return <>{fallback}</>
    }
  }

  return <>{children}</>
}

/**
 * 权限Hooks
 */
export const usePermission = () => {
  const permissions = useAppSelector(state => state.auth.permissions || [])
  const roles = useAppSelector(state => state.auth.roles || [])

  const hasPermission = (permission: string | string[]): boolean => {
    if (Array.isArray(permission)) {
      return permission.some(p => permissions.includes(p))
    }
    return permissions.includes(permission)
  }

  const hasRole = (role: string | string[]): boolean => {
    if (Array.isArray(role)) {
      return role.some(r => roles.includes(r))
    }
    return roles.includes(role)
  }

  return {
    hasPermission,
    hasRole,
    permissions,
    roles
  }
}

